import { i18n } from '../i18n.js';
/**
 * turn-banner.js
 * Transient "Your Turn" / "Opponent's Turn" band across the battle screen.
 * Non-blocking (pointer-events: none), self-removing, chimes on your turn.
 */


import { Banner } from './banner.js';
import { FX } from './fx.js';
import { SoundEffects } from './sound-effects.js';
let TurnBanner = {};

TurnBanner = (function () {
    const LIFETIME_MS = 1000;
    const REENTRY_GUARD_MS = 1200;

    let lastShownAt = 0;
    let lastKind = null;
    let removalTimer = null;
    let activeAnimation = null;

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        return translated && translated !== key ? translated : fallback;
    }

    function reducedMotion() {
        if (FX?.reducedMotion) return FX.reducedMotion();
        if (typeof window.matchMedia !== 'function') return false;
        try {
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch (_) {
            return false;
        }
    }

    function removeBanner() {
        if (activeAnimation) {
            try { activeAnimation.cancel(); } catch (_) { /* no-op */ }
            activeAnimation = null;
        }
        if (removalTimer) {
            clearTimeout(removalTimer);
            removalTimer = null;
        }
        const existing = document.getElementById('turn-banner');
        if (existing) existing.remove();
    }

    function show(kind) {
        if (kind !== 'your' && kind !== 'enemy') return;

        const now = Date.now();
        if (kind === lastKind && now - lastShownAt < REENTRY_GUARD_MS) return;
        lastShownAt = now;
        lastKind = kind;

        removeBanner();

        const banner = document.createElement('div');
        banner.id = 'turn-banner';
        banner.className = `turn-banner turn-banner--${kind}`;
        banner.setAttribute('role', 'status');
        banner.setAttribute('aria-live', 'polite');

        const text = document.createElement('span');
        text.className = 'turn-banner__text';
        text.textContent = kind === 'your'
            ? uiText('game.yourTurn', 'Your Turn')
            : uiText('game.opponentTurn', "Opponent's Turn");
        banner.appendChild(text);
        document.body.appendChild(banner);

        if (kind === 'your') SoundEffects?.play('turn_start');

        const slide = Math.round((window.innerWidth || 1400) * 0.04);
        if (typeof banner.animate === 'function') {
            // Easing goes on the keyframes (per segment): an options-level
            // easing would warp the WHOLE timeline and erase the hold phase.
            const frames = reducedMotion()
                ? [
                    { opacity: 0, easing: 'ease-out' },
                    { opacity: 1, offset: 0.15, easing: 'linear' },
                    { opacity: 1, offset: 0.75, easing: 'ease-in' },
                    { opacity: 0 },
                ]
                : (() => {
                    // Spring settle into center (shared engine curves), quiet
                    // hold, then a decisive exit along the same trajectory.
                    const shared = Banner?.easings?.() || {};
                    return [
                        { opacity: 0, transform: `translate(calc(-50% - ${slide}px), -50%)`, easing: shared.enter || 'cubic-bezier(0.2, 0.9, 0.3, 1)' },
                        { opacity: 1, transform: 'translate(-50%, -50%)', offset: 0.22, easing: 'linear' },
                        { opacity: 1, transform: 'translate(-50%, -50%)', offset: 0.75, easing: shared.exit || 'ease-in' },
                        { opacity: 0, transform: `translate(calc(-50% + ${slide}px), -50%)` },
                    ];
                })();
            try {
                const anim = banner.animate(frames, { duration: LIFETIME_MS });
                activeAnimation = anim;
                anim.finished
                    .then(() => {
                        if (activeAnimation === anim) activeAnimation = null;
                        if (banner.isConnected) banner.remove();
                    })
                    .catch(() => {
                        if (activeAnimation === anim) activeAnimation = null;
                    });
            } catch (_) { /* timed removal below still applies */ }
        }
        removalTimer = setTimeout(() => {
            if (banner.isConnected) banner.remove();
            removalTimer = null;
        }, LIFETIME_MS + 400);
    }

    function reset() {
        removeBanner();
        lastShownAt = 0;
        lastKind = null;
    }

    return { show, reset };
})();

export { TurnBanner };
